/**
 * Canonical, shareable locations for the Worldloom workspace.
 *
 * This module deliberately has no DOM dependencies so UI surfaces can adopt it
 * incrementally and its identity rules remain testable in isolation.
 */

export const WORKSPACE_PREFIX = "/workspace";

const PRESENTATION_KEYS = new Set([
  "status",
  "tab",
  "filter",
  "query",
  "type",
  "exception",
  "pane",
  "revision",
  "version",
  "compare",
  "successor",
  "retry",
]);

const ROUTE_SEGMENTS = {
  studio: [],
  worlds: ["worlds"],
  world: ["worlds", ":worldId"],
  section: ["worlds", ":worldId", "sections", ":sectionId"],
  feedback: ["worlds", ":worldId", "feedback", ":ticketId"],
  structures: ["structures"],
  structure: ["structures", ":structureId"],
  structureChange: ["structures", ":structureId", "change"],
  structureSession: ["structures", "sessions", ":conversationId"],
  preview: ["structures", ":structureId", "preview"],
  visualizer: ["visualizer"],
  board: ["board"],
  tickets: ["tickets"],
  ticket: ["tickets", ":ticketId"],
  assets: ["assets"],
  asset: ["assets", ":assetUid"],
  reviews: ["reviews"],
  ticketReview: ["reviews", "tickets", ":ticketId"],
  revisionReview: ["reviews", "revisions", ":revisionId"],
};

const ID_NAME_KEYS = {
  worldId: "worldName",
  sectionId: "sectionName",
  ticketId: "ticketName",
  structureId: "structureName",
  conversationId: "conversationName",
  assetUid: "assetName",
  revisionId: "revisionName",
};

function required(value, name) {
  const text = String(value ?? "").trim();
  if (!text) throw new Error(`${name} is required for this route`);
  return text;
}

function encodeSegment(value) {
  return encodeURIComponent(required(value, "route segment"));
}

function decodeSegment(value) {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

function identitySegment(id, name) {
  const encodedId = encodeSegment(id);
  return name ? `${slugifyProjectName(name)}~${encodedId}` : encodedId;
}

function decodeIdentitySegment(value) {
  const segment = String(value ?? "");
  const separator = segment.lastIndexOf("~");
  return decodeSegment(separator > 0 ? segment.slice(separator + 1) : segment);
}

/** Produce a readable, collision-safe project segment when combined with id. */
export function slugifyProjectName(projectName) {
  const normalized = String(projectName ?? "")
    .replaceAll("ø", "oe")
    .replaceAll("Ø", "Oe")
    .replaceAll("æ", "ae")
    .replaceAll("Æ", "Ae")
    .replaceAll("å", "aa")
    .replaceAll("Å", "Aa")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return normalized || "project";
}

export function buildProjectSegment(projectName, projectId) {
  return `${slugifyProjectName(projectName)}~${encodeSegment(projectId)}`;
}

function parseProjectSegment(segment) {
  const separator = String(segment ?? "").lastIndexOf("~");
  if (separator <= 0 || separator === segment.length - 1) return null;
  const slug = segment.slice(0, separator);
  const projectId = decodeSegment(segment.slice(separator + 1));
  return slug && projectId ? { projectSlug: slug, projectId } : null;
}

function presentationSearch(state) {
  const params = new URLSearchParams();
  for (const key of PRESENTATION_KEYS) {
    const value = state?.[key];
    if (value != null && String(value) !== "") params.set(key, String(value));
  }
  const search = params.toString();
  return search ? `?${search}` : "";
}

function presentationState(searchParams) {
  const state = {};
  for (const key of PRESENTATION_KEYS) {
    if (searchParams.has(key)) state[key] = searchParams.get(key);
  }
  return state;
}

/**
 * Build a canonical route for a project-scoped Worldloom object.
 * IDs are always route segments; presentation controls are always query state.
 */
export function buildCanonicalRoute({ projectName, projectId, route = "studio", state = {}, ...ids }) {
  const template = ROUTE_SEGMENTS[route];
  if (!template) throw new Error(`Unknown Worldloom route: ${route}`);
  const base = `${WORKSPACE_PREFIX}/${buildProjectSegment(projectName, projectId)}`;
  const path = template.map((segment) => {
    if (!segment.startsWith(":")) return segment;
    const key = segment.slice(1);
    return identitySegment(ids[key], ids[ID_NAME_KEYS[key]]);
  });
  return `${base}${path.length ? `/${path.join("/")}` : ""}${presentationSearch(state)}`;
}

/** Alias with a shorter name for UI call sites. */
export const buildRoute = buildCanonicalRoute;

function studioRoute(overrides = {}) {
  return {
    route: "studio",
    projectId: null,
    projectSlug: null,
    state: {},
    notFound: false,
    legacy: false,
    ...overrides,
  };
}

function parsedRoute(project, route, state, ids = {}) {
  return {
    route,
    projectId: project.projectId,
    projectSlug: project.projectSlug,
    state,
    notFound: false,
    legacy: false,
    ...ids,
  };
}

function parseKnownRoute(segments, project, state) {
  if (!segments.length || (segments.length === 1 && segments[0] === "studio")) return parsedRoute(project, "studio", state);
  if (segments.length === 1 && segments[0] === "worlds") return parsedRoute(project, "worlds", state);
  if (segments[0] === "worlds" && segments.length === 2) return parsedRoute(project, "world", state, { worldId: decodeIdentitySegment(segments[1]) });
  if (segments[0] === "worlds" && segments[2] === "sections" && segments.length === 4) return parsedRoute(project, "section", state, { worldId: decodeIdentitySegment(segments[1]), sectionId: decodeIdentitySegment(segments[3]) });
  if (segments[0] === "worlds" && segments[2] === "feedback" && segments.length === 4) return parsedRoute(project, "feedback", state, { worldId: decodeIdentitySegment(segments[1]), ticketId: decodeIdentitySegment(segments[3]) });
  if (segments.length === 1 && segments[0] === "structures") return parsedRoute(project, "structures", state);
  if (segments[0] === "structures" && segments[1] === "sessions" && segments.length === 3) return parsedRoute(project, "structureSession", state, { conversationId: decodeIdentitySegment(segments[2]) });
  if (segments[0] === "structures" && segments[2] === "change" && segments.length === 3) return parsedRoute(project, "structureChange", state, { structureId: decodeIdentitySegment(segments[1]) });
  if (segments[0] === "structures" && segments.length === 2) return parsedRoute(project, "structure", state, { structureId: decodeIdentitySegment(segments[1]) });
  if (segments[0] === "structures" && segments[2] === "preview" && segments.length === 3) return parsedRoute(project, "preview", state, { structureId: decodeIdentitySegment(segments[1]) });
  if (segments.length === 1 && segments[0] === "visualizer") return parsedRoute(project, "visualizer", state);
  if (segments.length === 1 && segments[0] === "board") return parsedRoute(project, "board", state);
  if (segments.length === 1 && segments[0] === "tickets") return parsedRoute(project, "tickets", state);
  if (segments[0] === "tickets" && segments.length === 2) return parsedRoute(project, "ticket", state, { ticketId: decodeIdentitySegment(segments[1]) });
  if (segments.length === 1 && segments[0] === "assets") return parsedRoute(project, "assets", state);
  if (segments[0] === "assets" && segments.length === 2) return parsedRoute(project, "asset", state, { assetUid: decodeIdentitySegment(segments[1]) });
  if (segments.length === 1 && segments[0] === "reviews") return parsedRoute(project, "reviews", state);
  if (segments[0] === "reviews" && segments[1] === "tickets" && segments.length === 3) return parsedRoute(project, "ticketReview", state, { ticketId: decodeIdentitySegment(segments[2]) });
  if (segments[0] === "reviews" && segments[1] === "revisions" && segments.length === 3) return parsedRoute(project, "revisionReview", state, { revisionId: decodeIdentitySegment(segments[2]) });
  return null;
}

/**
 * Parse a pathname, URL, or Location-like value. Unknown paths deliberately
 * fall back to Studio so a bad link never leaves the SPA on a blank surface.
 */
export function parseCanonicalRoute(input, base = "https://worldloom.invalid") {
  const raw = input instanceof URL ? input.href : typeof input === "string" ? input : `${input?.pathname ?? ""}${input?.search ?? ""}${input?.hash ?? ""}`;
  let url;
  try {
    url = new URL(raw || "/", base);
  } catch {
    return studioRoute({ notFound: true });
  }

  // Earlier releases selected an asset with a bare hash. Keep those links
  // usable until UI integration can replace them with project-scoped routes.
  const hash = url.hash.slice(1);
  if (url.pathname === "/" && hash && !hash.startsWith("/")) {
    return studioRoute({ route: "asset", assetUid: decodeSegment(hash), legacy: true });
  }

  const path = hash.startsWith("/") ? hash : url.pathname;
  const segments = path.split("/").filter(Boolean);
  if (!segments.length) return studioRoute({ state: presentationState(url.searchParams) });
  if (segments[0] !== WORKSPACE_PREFIX.slice(1) || segments.length < 2) return studioRoute({ notFound: true, state: presentationState(url.searchParams) });

  const project = parseProjectSegment(segments[1]);
  if (!project) return studioRoute({ notFound: true, state: presentationState(url.searchParams) });
  const state = presentationState(url.searchParams);
  const route = parseKnownRoute(segments.slice(2), project, state);
  if (route && Object.values(route).every((value) => value !== null)) return route;
  return studioRoute({ ...project, state, notFound: true });
}

export const parseRoute = parseCanonicalRoute;

/** Return the stable project identity from any canonical project route. */
export function extractProjectId(input) {
  return parseCanonicalRoute(input).projectId;
}

/** Whether a click may be intercepted for same-tab client-side navigation. */
export function isUnmodifiedPrimaryLinkClick(event) {
  return Boolean(event)
    && event.button === 0
    && !event.defaultPrevented
    && !event.metaKey
    && !event.ctrlKey
    && !event.shiftKey
    && !event.altKey;
}
